<template>
  <div class="quota-wrapper">
    <div class="container" v-if="!loading">
      <div class="quota-info">
        <p>
          {{ parseTranslation(TITLE_HOME) }} >
          <span>{{ parseTranslation(SAMPLE_SETTINGS) }}</span>
        </p>

        <div>
          <h1>{{ parseTranslation(SAMPLE_SETTINGS) }}</h1>

          <router-link
            :to="{ name: 'report', query: { survey: $route.query.survey } }"
            class="report"
          >
            <Report />
            Report
          </router-link>
        </div>
      </div>

      <div class="switch" v-if="!surveyTypeIsNotCAWI">
        <div class="toggle-panel">
          <div>
            <p>
              {{ parseTranslation(USE_PANEL) }}
            </p>

            <div :class="{ active: option, disabled: disableToggle() }">
              <button
                :class="{ active: !option }"
                @click="option = false"
                :disabled="disableToggle()"
              >
                {{ parseTranslation(NO) }}
              </button>

              <button
                :class="{ active: option }"
                @click="option = true"
                :disabled="disableToggle()"
              >
                {{ parseTranslation(YES) }}
              </button>
            </div>
          </div>

          <p class="message-choose-panel">
            <span v-if="!disableToggle()">
              {{ parseTranslation(OPTION_NOT_SELECTED) }}
            </span>
            <span v-else> {{ parseTranslation(OPTION_SELECTED) }} </span>
          </p>
        </div>

        <div class="status" v-if="sampleStatus">
          <span :class="{ paused: sampleStatus === 'pause' }">{{
            parseTranslation(sampleStatus === 'live' ? LIVE : PAUSED)
          }}</span>
          <div class="status-info">
            <p
              v-html="
                parseTranslation(
                  sampleStatus === 'live' ? SAMPLE_LIVE_INFO : SAMPLE_PAUSED_INFO,
                )
              "
            ></p>
          </div>
        </div>

        <div class="copy-link" v-if="showQuestionLink()">
          <div>
            <p>Link Questions</p>

            <p :title="questionURL()">{{ questionURL() | maxLength }}</p>
          </div>

          <button @click="copyToClipboard(questionURL()), changeTextButton()">
            <span v-if="copied"> {{ parseTranslation(COPIED) }} </span>
            <span v-else> {{ parseTranslation(COPY_URL) }} </span>
          </button>
        </div>
      </div>

      <div>
        <div class="option-2 fade" :class="{ active: !option }">
          <OwnPanel
            :survey="survey"
            :getSurvey="getSurvey"
            :surveyTypeIsNotCAWI="surveyTypeIsNotCAWI"
          />
        </div>

        <div :class="{ active: option }" class="option-2 fade">
          <OfferwisePanel :survey="survey" :getSurvey="getSurvey" />
        </div>
      </div>
    </div>

    <Loader v-if="loading || isLoadingSaveSettingsQuota" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import copyToClipboard from 'copy-to-clipboard'

import Loader from '@/components/loading/Loader'

import OwnPanel from './OwnPanel'
import OfferwisePanel from './OfferwisePanel'

import { SURVEY_TYPE } from '@/utils/constants'

import * as translationQuota from './index.translations'

import Report from '@/components/imgs/Report'

const { default: translations, ...translationKeys } = translationQuota

export default {
  name: 'Quota',

  components: {
    OwnPanel,
    OfferwisePanel,
    Loader,
    Report,
  },

  filters: {
    maxLength(value) {
      if (value.length > 38) {
        return (
          value
            .split('')
            .splice(0, 38)
            .join('') + '...'
        )
      } else return value
    },
  },

  data() {
    return {
      translations,
      ...translationKeys,
      option: true,
      survey: null,
      loading: false,
      copied: false,
      sampleStatus: null,
      surveyTypeIsNotCAWI: false,
    }
  },

  computed: {
    ...mapGetters(['hasProjectCreated', 'isLoadingSaveSettingsQuota']),
  },

  methods: {
    ...mapActions(['executeSurveyAPICall']),

    async getSurvey() {
      this.loading = true

      const survey = await this.executeSurveyAPICall({
        path: 'survey',
        queryParams: { survey_id: this.$route.query.survey },
      })

      if (!survey) return (this.loading = false)

      this.survey = survey

      this.sampleStatus = survey.sample?.status

      this.surveyTypeIsNotCAWI = [SURVEY_TYPE.CATI, SURVEY_TYPE.CAPI].includes(
        survey.survey_type,
      )

      if ((!survey?.sample && survey?.quota) || this.surveyTypeIsNotCAWI) {
        this.option = false
      } else {
        this.option = true
      }

      this.loading = false
    },

    disableToggle() {
      const quota = !!this.survey?.quota

      const sample = !!this.survey?.sample

      return quota || sample
    },

    copyToClipboard,

    changeTextButton() {
      const THREE_SECOND = 3000

      this.copied = true

      setTimeout(() => {
        this.copied = false
      }, THREE_SECOND)
    },

    questionURL() {
      let URL = this.survey.answer_url

      if (this.hasProjectCreated) {
        URL = `${URL}?owid=`
      }

      return URL
    },

    showQuestionLink() {
      return (this.hasProjectCreated || this.survey?.quota) && this.option
    },
  },

  created() {
    this.getSurvey()
  },
}
</script>

<style lang="scss" scoped>
.quota-info {
  padding: 30px 0 20px;
  border-bottom: 1px solid $white;

  p {
    @include tipography(16);

    margin: 3px 0 0 15px;

    @include d(s) {
      margin: 10px 0 0;
    }
  }

  div {
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;

    h1 {
      @include tipography(26);
      font-weight: bold;
    }

    .report {
      @include tipography(14);
    }
  }

  > p {
    @include tipography(14);
    color: $gray-2;
    span {
      color: $white;
    }
  }

  .light & {
    border-bottom: 1px solid $gray-5;
    h1,
    span,
    .report {
      color: $gray-6;
    }

    p {
      color: $gray;
    }
  }
}

.switch {
  @include flexAlignCenter;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-top: 40px;

  .toggle-panel {
    > div {
      @include flexAlignCenter;
      @include tipography(16);
      font-weight: bold;
      letter-spacing: 0.3px;
      text-transform: uppercase;
      white-space: nowrap;

      .light & {
        color: $main;
      }

      p {
        margin: 10px 0;
      }

      div {
        margin-left: 20px;
        border: 1px solid $gray-5;
        border-radius: 50px;
        position: relative;
        transition: all 0.3s ease;

        &::after {
          content: 'yes';
          background: $green-2;
          border-radius: 50px;
          padding: 6px 30px;
          position: absolute;
          left: 3px;
          top: 3px;
          color: $transparent;
          transition: all 0.3s ease;
        }

        &.active {
          &::after {
            left: calc(100% - 90px);
          }
        }

        button {
          position: relative;
          z-index: 2;
          padding: 8px 30px;
          background: none;
          border: none;
          cursor: pointer;
          border-radius: 50px;
          transition: all 0.3s ease;
          @include tipography(14);
          font-weight: bold;
          text-transform: uppercase;
          &.active {
            color: $white;
            &:hover {
              color: $gray-light-2;
            }
          }
        }

        &.disabled {
          opacity: 0.5;
        }
      }

      .light & {
        button {
          color: $gray-2;
          &:hover {
            color: $gray-8;
          }
        }
      }
    }

    .message-choose-panel {
      @include tipography(14);
      margin-top: 3px;

      .light & {
        color: $gray-2;
      }
    }
  }

  .status {
    @include sample-status;
    margin-right: 30px;

    @media (max-width: 582.98px) {
      margin: 16px 30px 0 0;

      .status-info {
        left: 0;
        width: 180px;
      }
    }
  }

  .copy-link {
    @include flexMiddle;
    padding: 8px;
    border-radius: 3px;
    background: $gray-light-3;
    border: 1px solid $gray-light-2;

    > div {
      border-right: 1px solid $gray-2;
      padding-right: 8px;
      margin-right: 8px;
      p {
        &:nth-of-type(1) {
          @include tipography(14);
          color: $blue;
          margin-bottom: 5px;
        }

        &:nth-of-type(2) {
          @include tipography(11);
          max-width: 200px;
        }
      }
    }

    button {
      border: none;
      background: $transparent;
      @include tipography(14);
      color: $blue;

      &:focus {
        outline: none;
      }
    }

    .light & {
      > div {
        p {
          &:nth-of-type(2) {
            color: $gray-2;
          }
        }
      }
    }
  }
}

.message-choose-panel {
  @include tipography(14);
  margin-top: 5px;

  .light & {
    color: $gray-2;
  }
}

.option-1,
.option-2 {
  display: none;
  animation: growDown 400ms ease-in-out forwards;
  transform-origin: top center;

  &.active {
    display: block;
  }
}

@media (max-width: 759.98px) {
  .copy-link {
    margin-top: 20px;
  }
}
</style>
